import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, Image, Trash2, Printer, X, Eye, AlertCircle } from 'lucide-react';

// 파일 업로더 컴포넌트
const FileUploader = ({ onFileUpload, supportedTypes }) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    onFileUpload(droppedFiles);
  }, [onFileUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleFileInput = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    onFileUpload(selectedFiles);
    e.target.value = '';
  }, [onFileUpload]);

  return (
    <>
      <div className="info-box">
        <div className="info-header">
          <AlertCircle className="w-5 h-5" />
          <span className="font-semibold">지원 파일 형식 및 인쇄 안내</span>
        </div>
        <p className="info-text">
          <strong>이미지:</strong> JPG, PNG, GIF, BMP, WebP &nbsp;|&nbsp;
          <strong>문서:</strong> PDF, TXT &nbsp;|&nbsp;
          <strong>참고:</strong> HWP 파일은 지원되지 않습니다 (PDF로 변환 후 사용하세요)
        </p>
        <p className="info-text" style={{marginTop: '8px', fontSize: '0.8rem', color: '#92400e'}}>
          💡 <strong>인쇄 팁:</strong> 브라우저에서 팝업을 허용해주세요. 인쇄가 안 될 경우 브라우저 설정을 확인해주세요.
        </p>
      </div>

      <div 
        className={`upload-area ${isDragOver ? 'dragover' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="upload-icon" />
        <h3>파일을 여기에 드래그하거나 클릭하여 선택</h3>
        <p>여러 파일을 한 번에 선택할 수 있습니다</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={supportedTypes.join(',')}
          onChange={handleFileInput}
          className="hidden"
        />
      </div>
    </>
  );
};

// 파일 카드 컴포넌트
const FileCard = ({ file, onRemove, onPreview, onPrint }) => {
  const getFileIcon = (file) => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
    if (imageTypes.includes(file.type)) {
      return <Image className="w-5 h-5 text-blue-500" />;
    }
    return <FileText className="w-5 h-5 text-green-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImage = (file) => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
    return imageTypes.includes(file.type);
  };

  return (
    <div className="file-card">
      <div className="file-header">
        <div className="file-info">
          {getFileIcon(file)}
          <span className="file-name" title={file.name}>{file.name}</span>
        </div>
        <button
          onClick={() => onRemove(file.id)}
          className="btn-remove"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* 이미지 썸네일 미리보기 */}
      {isImage(file) && file.url && (
        <div className="image-preview">
          <img 
            src={file.url} 
            alt={file.name}
            className="thumbnail"
            onClick={() => onPreview(file)}
          />
        </div>
      )}

      <p className="file-size">{formatFileSize(file.size)}</p>

      <div className="file-actions">
        <button
          onClick={() => onPreview(file)}
          className="btn btn-preview"
        >
          <Eye className="w-4 h-4" />
          미리보기
        </button>
        <button
          onClick={() => onPrint(file)}
          className="btn btn-print"
        >
          <Printer className="w-4 h-4" />
          인쇄
        </button>
      </div>
    </div>
  );
};

// 파일 목록 컴포넌트
const FileList = ({ files, onRemove, onPreview, onPrint, onPrintAll }) => {
  if (files.length === 0) {
    return (
      <div className="empty-state">
        <FileText className="empty-icon" />
        <h3>아직 업로드된 파일이 없습니다</h3>
        <p>위의 업로드 영역을 사용하여 파일을 추가해보세요</p>
      </div>
    );
  }

  return (
    <div className="files-section">
      <div className="files-header">
        <h2>업로드된 파일 ({files.length}개)</h2>
        <button
          onClick={onPrintAll}
          className="btn-print-all"
        >
          <Printer className="w-5 h-5" />
          전체 인쇄
        </button>
      </div>

      <div className="files-grid">
        {files.map((file) => (
          <FileCard
            key={file.id}
            file={file}
            onRemove={onRemove}
            onPreview={onPreview}
            onPrint={onPrint}
          />
        ))}
      </div>
    </div>
  );
};

// 미리보기 모달 컴포넌트
const PreviewModal = ({ file, onClose }) => {
  if (!file) return null;

  const renderPreviewContent = () => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
    
    if (imageTypes.includes(file.type)) {
      return <img src={file.url} alt={file.name} className="preview-image" />;
    } else if (file.type === 'application/pdf') {
      return <iframe src={file.url} title={file.name} className="preview-iframe" />;
    } else if (file.type === 'text/plain') {
      return <pre className="preview-text">{file.content || '로딩 중...'}</pre>;
    }
    
    return <div>미리보기를 지원하지 않는 파일 형식입니다.</div>;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{file.name}</h3>
          <button onClick={onClose} className="btn-close">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="modal-body">
          {renderPreviewContent()}
        </div>
      </div>
    </div>
  );
};

// 메인 앱 컴포넌트
const BatchPrintApp = () => {
  const [files, setFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [fileIdCounter, setFileIdCounter] = useState(0);

  // 지원하는 파일 형식
  const supportedFormats = {
    images: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'],
    documents: ['application/pdf', 'text/plain']
  };

  const getAllSupportedTypes = () => [
    ...supportedFormats.images,
    ...supportedFormats.documents
  ];

  const isFileSupported = (file) => {
    return getAllSupportedTypes().includes(file.type);
  };

  // 파일 업로드 처리
  const handleFileUpload = useCallback(async (uploadedFiles) => {
    const newFiles = [];
    
    for (let file of uploadedFiles) {
      if (!isFileSupported(file)) {
        alert(`지원하지 않는 파일 형식입니다: ${file.name}\n지원 형식: 이미지(JPG, PNG, GIF 등), PDF, TXT`);
        continue;
      }

      const fileData = {
        id: fileIdCounter + newFiles.length + 1,
        file: file,
        name: file.name,
        type: file.type,
        size: file.size,
        url: null,
        content: null
      };

      // 파일 타입별로 처리
      if (supportedFormats.images.includes(file.type)) {
        fileData.url = URL.createObjectURL(file);
      } else if (file.type === 'application/pdf') {
        fileData.url = URL.createObjectURL(file);
      } else if (file.type === 'text/plain') {
        try {
          const text = await file.text();
          fileData.content = text;
        } catch (error) {
          fileData.content = '파일을 읽을 수 없습니다.';
        }
      }

      newFiles.push(fileData);
    }

    setFiles(prev => [...prev, ...newFiles]);
    setFileIdCounter(prev => prev + newFiles.length);
  }, [fileIdCounter, supportedFormats]);

  // 파일 제거
  const handleRemoveFile = useCallback((id) => {
    setFiles(prev => {
      const updatedFiles = prev.filter(file => file.id !== id);
      const fileToRemove = prev.find(file => file.id === id);
      if (fileToRemove && fileToRemove.url) {
        URL.revokeObjectURL(fileToRemove.url);
      }
      return updatedFiles;
    });
  }, []);

  // 미리보기 열기
  const handleOpenPreview = useCallback((file) => {
    setPreviewFile(file);
  }, []);

  // 미리보기 닫기
  const handleClosePreview = useCallback(() => {
    setPreviewFile(null);
  }, []);

  // 파일 인쇄
  const handlePrintFile = useCallback((file) => {
    try {
      if (supportedFormats.images.includes(file.type)) {
        // 이미지 인쇄 - iframe 방식 사용
        printImage(file);
      } else if (file.type === 'application/pdf') {
        // PDF 인쇄 - 새 탭에서 열기
        printPDF(file);
      } else if (file.content) {
        // 텍스트 인쇄 - iframe 방식 사용
        printText(file);
      }
    } catch (error) {
      console.error('인쇄 중 오류가 발생했습니다:', error);
      alert('인쇄 중 오류가 발생했습니다. 브라우저의 팝업 차단을 해제해주세요.');
    }
  }, [supportedFormats]);

  // 이미지 인쇄 함수
  const printImage = (file) => {
    // 숨겨진 iframe 생성
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>인쇄 - ${file.name}</title>
          <style>
            body { margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
            img { max-width: 100%; max-height: 100%; object-fit: contain; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <img src="${file.url}" alt="${file.name}">
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // 이미지 로드 후 인쇄
    iframe.onload = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          // 인쇄 후 iframe 제거
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        } catch (error) {
          console.error('iframe 인쇄 실패:', error);
          // 대안 방법: 새 창으로 열기
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            newWindow.document.write(`
              <html>
                <head><title>인쇄 - ${file.name}</title></head>
                <body style="margin: 0; text-align: center;">
                  <img src="${file.url}" style="max-width: 100%; height: auto;">
                  <script>
                    window.onload = function() {
                      setTimeout(function() { window.print(); }, 500);
                    }
                  </script>
                </body>
              </html>
            `);
            newWindow.document.close();
          } else {
            alert('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.');
          }
          document.body.removeChild(iframe);
        }
      }, 500);
    };
  };

  // PDF 인쇄 함수
  const printPDF = (file) => {
    // PDF는 새 탭에서 열어서 브라우저 기본 인쇄 사용
    const newWindow = window.open(file.url, '_blank');
    if (!newWindow) {
      alert('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용하거나, 파일을 우클릭하여 "새 탭에서 열기"를 선택해주세요.');
      return;
    }
    
    // PDF가 로드된 후 인쇄 다이얼로그 자동 실행 (선택사항)
    newWindow.onload = () => {
      setTimeout(() => {
        try {
          newWindow.print();
        } catch (error) {
          console.log('PDF 자동 인쇄 실패 (정상 동작)');
        }
      }, 1000);
    };
  };

  // 텍스트 인쇄 함수
  const printText = (file) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>인쇄 - ${file.name}</title>
          <style>
            body { font-family: 'Malgun Gothic', 'Arial', sans-serif; padding: 20px; line-height: 1.6; }
            pre { white-space: pre-wrap; word-wrap: break-word; }
            @media print { body { margin: 0; padding: 15px; } }
          </style>
        </head>
        <body>
          <h3>${file.name}</h3>
          <pre>${file.content}</pre>
        </body>
      </html>
    `);
    iframeDoc.close();
    
    iframe.onload = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        } catch (error) {
          console.error('텍스트 인쇄 실패:', error);
          document.body.removeChild(iframe);
          alert('인쇄에 실패했습니다.');
        }
      }, 300);
    };
  };

  // 전체 파일 인쇄
  const handlePrintAllFiles = useCallback(() => {
    if (files.length === 0) {
      alert('인쇄할 파일이 없습니다.');
      return;
    }

    const userConfirm = window.confirm(`총 ${files.length}개의 파일을 일괄 인쇄하시겠습니까?\n\n파일은 1초 간격으로 순차적으로 인쇄됩니다.\n브라우저에서 팝업을 허용해주세요.`);
    
    if (!userConfirm) return;

    files.forEach((file, index) => {
      setTimeout(() => {
        console.log(`인쇄 중... (${index + 1}/${files.length}): ${file.name}`);
        handlePrintFile(file);
      }, index * 1500); // 1.5초 간격으로 여유있게 설정
    });

    // 진행상황 표시
    const totalTime = files.length * 1.5;
    alert(`일괄 인쇄가 시작되었습니다.\n예상 소요 시간: 약 ${Math.ceil(totalTime)}초\n\n각 파일의 인쇄 다이얼로그가 순차적으로 나타납니다.`);
  }, [files, handlePrintFile]);

  return (
    <div className="app">
      <div className="container">
        {/* 헤더 */}
        <div className="header">
          <h1>📄 일괄 파일 인쇄</h1>
          <p>여러 파일을 한 번에 업로드하고 일괄로 인쇄하세요</p>
        </div>

        {/* 파일 업로더 */}
        <FileUploader 
          onFileUpload={handleFileUpload}
          supportedTypes={getAllSupportedTypes()}
        />

        {/* 파일 목록 */}
        <FileList
          files={files}
          onRemove={handleRemoveFile}
          onPreview={handleOpenPreview}
          onPrint={handlePrintFile}
          onPrintAll={handlePrintAllFiles}
        />

        {/* 미리보기 모달 */}
        {previewFile && (
          <PreviewModal
            file={previewFile}
            onClose={handleClosePreview}
          />
        )}
      </div>

      <style jsx>{`
        .app {
          min-height: 100vh;
          background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%);
          padding: 24px;
          font-family: 'Malgun Gothic', '맑은 고딕', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .header {
          text-align: center;
          margin-bottom: 32px;
        }

        .header h1 {
          font-size: 2.5rem;
          color: #1f2937;
          margin-bottom: 12px;
          font-weight: 700;
        }

        .header p {
          color: #6b7280;
          font-size: 1.125rem;
        }

        .info-box {
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 24px;
        }

        .info-header {
          color: #92400e;
          font-weight: 600;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .info-text {
          color: #92400e;
          font-size: 0.875rem;
          margin: 0;
        }

        .upload-area {
          border: 2px dashed #3b82f6;
          border-radius: 12px;
          padding: 48px;
          text-align: center;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
          margin-bottom: 32px;
        }

        .upload-area:hover {
          background: #f0f4ff;
        }

        .upload-area.dragover {
          border-color: #1d4ed8;
          background: #eff6ff;
        }

        .upload-icon {
          width: 4rem;
          height: 4rem;
          color: #60a5fa;
          margin: 0 auto 16px;
        }

        .upload-area h3 {
          font-size: 1.25rem;
          color: #374151;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .upload-area p {
          color: #6b7280;
          margin: 0;
        }

        .files-section {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          padding: 24px;
          margin-bottom: 32px;
        }

        .files-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .files-header h2 {
          font-size: 1.5rem;
          color: #1f2937;
          font-weight: 700;
          margin: 0;
        }

        .btn-print-all {
          background: #059669;
          color: white;
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .btn-print-all:hover {
          background: #047857;
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(5, 150, 105, 0.3);
        }

        .btn-print-all:active {
          transform: translateY(0);
          box-shadow: 0 3px 6px rgba(5, 150, 105, 0.3);
        }

        .files-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }

        .file-card {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          transition: box-shadow 0.3s;
          background: white;
        }

        .file-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .file-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .file-info {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 0;
        }

        .file-name {
          font-weight: 500;
          color: #1f2937;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .image-preview {
          margin: 12px 0;
          border-radius: 8px;
          overflow: hidden;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
        }

        .thumbnail {
          width: 100%;
          height: 150px;
          object-fit: cover;
          cursor: pointer;
          transition: transform 0.2s ease;
          display: block;
        }

        .thumbnail:hover {
          transform: scale(1.02);
        }

        .file-size {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0 0 12px 0;
        }

        .file-actions {
          display: flex;
          gap: 8px;
        }

        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          flex: 1;
        }

        .btn-preview {
          background: #3b82f6;
          color: white;
        }

        .btn-preview:hover {
          background: #2563eb;
        }

        .btn-print {
          background: #10b981;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .btn-print:hover {
          background: #059669;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
        }

        .btn-print:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
        }

        .btn-remove {
          background: none;
          color: #dc2626;
          padding: 4px;
          border: none;
          cursor: pointer;
        }

        .btn-remove:hover {
          color: #991b1b;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-title {
          font-size: 1.125rem;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin: 0;
        }

        .btn-close {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 4px;
        }

        .btn-close:hover {
          color: #374151;
        }

        .modal-body {
          padding: 24px;
          overflow: auto;
          max-height: calc(90vh - 80px);
        }

        .preview-image {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0 auto;
        }

        .preview-iframe {
          width: 100%;
          height: 500px;
          border: 1px solid #e5e7eb;
        }

        .preview-text {
          background: #f9fafb;
          padding: 16px;
          border-radius: 6px;
          white-space: pre-wrap;
          font-size: 0.875rem;
          margin: 0;
        }

        .empty-state {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          padding: 48px;
          text-align: center;
        }

        .empty-icon {
          width: 4rem;
          height: 4rem;
          color: #d1d5db;
          margin: 0 auto 16px;
        }

        .empty-state h3 {
          color: #6b7280;
          margin-bottom: 8px;
          font-size: 1.25rem;
        }

        .empty-state p {
          color: #9ca3af;
          margin: 0;
        }

        .hidden {
          display: none !important;
        }

        @media (max-width: 768px) {
          .files-grid {
            grid-template-columns: 1fr;
          }
          
          .files-header {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }
          
          .upload-area {
            padding: 32px 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default BatchPrintApp;